import React, { useState, useEffect } from 'react';
import {
  Box, Container, Paper, Typography, Button, TextField,
  Alert, CircularProgress, InputAdornment, IconButton,
  Stack, Divider, useTheme,
} from '@mui/material';
import { LogIn, Eye, EyeOff, Mail, Lock } from 'lucide-react';
import logo from '../assets/roshan-grams-white-logo.png';
import logoDark from '../assets/roshan-grams-black-logo.png';
import { loginUser } from '../firebase/authService';
import { getBillingSettings } from '../firebase/settingsService';

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [companyName, setCompanyName] = useState('');
  const theme = useTheme();

  const currentLogo = theme.palette.mode === 'dark' ? logoDark : logo;

  useEffect(() => {
    getBillingSettings().then((result) => {
      if (result.success && result.data?.companyName) {
        setCompanyName(result.data.companyName);
      }
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await loginUser(email, password);
    if (result.success) {
      onLogin(result.user);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: theme.palette.mode === 'dark'
          ? 'linear-gradient(135deg, #000000 0%, #0f0f0f 100%)'
          : 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
      }}
    >
      <Container maxWidth="xs">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.shadows[4],
          }}
        >
          {/* Logo */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <Box
              component="img"
              src={currentLogo}
              alt="Roshan Murukku"
              sx={{ width: 120, height: 'auto', objectFit: 'contain', mb: 2 }}
            />
            <Typography variant="body2" color="text.secondary">
              Sign in to access your dashboard
            </Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {error && (
            <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Stack spacing={2.5}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                autoComplete="email"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Mail size={18} color="gray" />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                autoComplete="current-password"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock size={18} color="gray" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        disabled={loading}
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                fullWidth
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                startIcon={
                  loading
                    ? <CircularProgress size={18} color="inherit" />
                    : <LogIn size={18} />
                }
                sx={{ py: 1.4, fontSize: '0.95rem' }}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </Stack>
          </form>

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Need help? Contact your administrator
            </Typography>
          </Box>

          <Divider sx={{ my: 2.5 }} />

          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ display: 'block', textAlign: 'center' }}
          >
            © {new Date().getFullYear()} {companyName || 'Roshan Gram Store'}. All rights reserved.
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default Login;
